import { Banknote, PiggyBank, Pencil, Receipt, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

import { EditBudgetCapPopup } from "./popup/EditBudgetCapPopup";

import { Button } from "@/components/ui/v2/button";
import MetricCard from "@/features/campaigns/jobboards/components/MetricCard";
import { usePopup } from "@/lib/hooks/usePopup";
import { PaymentTypeEnum } from "@/types/client/ClientType";
import { formatCurrency } from "@/utils/valueFormat";

interface PostpaidBudgetTileRowProps {
  allocableBudget?: number;
  allocatedBudget?: number;
  budgetCap?: number;
  clientId: string;
  isLoading?: boolean;
  month: string;
  monthlyBudget?: number;
  onEditSuccess?: () => void;
  paymentType?: PaymentTypeEnum;
  spent?: number;
  year: string;
}

const PostpaidBudgetTileRow = ({
  allocableBudget,
  allocatedBudget,
  budgetCap,
  clientId,
  isLoading = false,
  month,
  onEditSuccess,
  spent,
  year,
}: PostpaidBudgetTileRowProps) => {
  const { t } = useTranslation();
  const { openPopup, resetPopup } = usePopup();

  const handleEditClick = () => {
    openPopup(
      <EditBudgetCapPopup
        budget={budgetCap || 0}
        clientId={clientId}
        month={month}
        year={year}
        onSuccess={() => {
          onEditSuccess?.();
          resetPopup();
        }}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        variant="gradient"
        icon={<Wallet className="size-5 text-primary" />}
        isLoading={isLoading}
        label={`${t("common.budget")} ${t("common.cap")}`}
        value={formatCurrency(budgetCap ?? 0)}
        action={
          <Button
            aria-label={`Edit ${t("common.budget")} ${t("common.cap")}`}
            size="icon-sm"
            variant="ghost"
            onClick={handleEditClick}
          >
            <Pencil className="size-4" />
          </Button>
        }
      />
      <MetricCard
        variant="gradient"
        icon={<Receipt className="size-5 text-primary" />}
        isLoading={isLoading}
        label={`${t("common.allocated")} ${t("common.budget")}`}
        value={formatCurrency(allocatedBudget ?? 0)}
      />
      <MetricCard
        variant="gradient"
        icon={<Banknote className="size-5 text-primary" />}
        isLoading={isLoading}
        label={`${t("common.allocable")} ${t("common.budget")}`}
        value={formatCurrency(allocableBudget ?? 0)}
      />
      <MetricCard
        variant="gradient"
        icon={<PiggyBank className="size-5 text-primary" />}
        isLoading={isLoading}
        label={`${t("common.spent")}`}
        value={formatCurrency(spent ?? 0)}
      />
    </div>
  );
};

export default PostpaidBudgetTileRow;
